import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { Event, RoleGrantRow, Session, SubEvent } from '../../types';
import { ShareEventDialog } from '../../components/ShareEventDialog';
import { errorText } from '../errors';
import { Pair, useOrganizer } from '../i18n';
import { Btn } from '../ui';
import { toLocalInput } from '../EventDraftFields';
import { BackLink, EventHeading, PlusGlyph, Sheet, Stepper } from './chrome';
import { CoHostDialog, Field, inputClass } from './CoHostDialog';
import { AddAgendaDialog } from './AddAgendaDialog';
import { SpeakersStep } from './SpeakersStep';
import { GroupedAgenda, SubcategoryEditor } from './subEvents';
import { dayOfSession, daysOf } from './days';
import { PeopleEmpty, PeopleHeading, PersonRow, initialsOf } from './people';
import { whenLine } from './EventsDashboard';

const STEPS: Pair[] = [
  { ne: 'कार्यक्रमको विवरण', en: 'Event Details' },
  { ne: 'कार्यसूची', en: 'Agendas' },
  { ne: 'वक्ताहरू', en: 'Speakers' },
  { ne: 'मानिस', en: 'Peoples' },
];

/** The last step, which is the one that finishes rather than continues. */
const LAST = STEPS.length - 1;

/**
 * The two actions every step ends on.
 *
 * The design gives each step the same bar: a way out on the left and the
 * step's own action on the right, both filling the width. The old row of
 * Back and "Next: Sessions" named the step it went to, which read as a
 * label for the screen you were leaving rather than the thing you were
 * about to do.
 */
const Actions: React.FC<{
  cancel: string;
  go: string;
  onCancel: () => void;
  onGo: () => void;
  disabled?: boolean;
}> = ({ cancel, go, onCancel, onGo, disabled }) => (
  <div className="border-t-[0.6px] border-[#f3f4f6] pt-4 flex gap-9">
    <button
      onClick={onCancel}
      className="flex-1 h-[41px] bg-white border-[0.6px] border-line rounded-[8px]
        text-[14px] leading-5 text-[#364153] hover:border-navy-800"
    >
      {cancel}
    </button>
    <button
      onClick={onGo}
      disabled={disabled}
      className="flex-1 h-[41px] bg-navy-800 hover:bg-navy-700 rounded-[8px]
        text-[14px] leading-5 text-white disabled:opacity-50"
    >
      {go}
    </button>
  </div>
);

interface Props {
  /** Absent when building a new one; present when changing one that exists. */
  event?: Event;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

/**
 * Making an event, in the three passes the design lays out.
 *
 * The first pass is the only one that has to happen: what the event is
 * called, when it is, and where. The running order and the people can be
 * skipped and added later, which is why every step after the first offers
 * a way past it.
 */
export const EventWizard: React.FC<Props> = ({ event, onClose, onSaved }) => {
  const { t, num } = useOrganizer();
  const { user } = useAuthStore();
  /** Nine tomorrow morning, which is what most events want offered. */
  const defaultStart = (() => {
    const at = new Date();
    at.setDate(at.getDate() + 1);
    at.setHours(9, 0, 0, 0);
    return toLocalInput(at);
  })();
  const plusAnHour = (from: string) =>
    toLocalInput(new Date(+new Date(from) + 60 * 60000));

  const [step, setStep] = useState(0);
  const [saved, setSaved] = useState<Event | null>(event ?? null);

  const [title, setTitle] = useState(event?.title ?? '');
  const [venue, setVenue] = useState(event?.venue ?? '');
  const [description, setDescription] = useState(event?.description ?? '');
  /**
   * Whether this event's hour is still a plan.
   *
   * Once it has opened it is not: people came at it, the register is
   * timed from it, and the running order was laid out against it. The
   * server refuses to move it, so the field says so rather than letting
   * somebody type a new one and be told no on save.
   */
  const hasOpened = !!event && (
    !!event.started_at
    || event.status === 'active'
    || event.status === 'ended'
  );

  const [startsAt, setStartsAt] = useState(
    event?.scheduled_start ? toLocalInput(new Date(event.scheduled_start)) : defaultStart
  );
  const [endsAt, setEndsAt] = useState(
    event?.scheduled_end
      ? toLocalInput(new Date(event.scheduled_end))
      : plusAnHour(defaultStart)
  );
  const [busy, setBusy] = useState(false);

  const [drafting, setDrafting] = useState(false);
  /** The talk being changed, if one is. */
  const [editing, setEditing] = useState<Session | null>(null);

  /**
   * The named parts of the day, and the ones typed before it existed.
   *
   * The design asks for them on the first step, which is the step that
   * creates the event - so on a brand-new one there is nothing yet to
   * hang a heading off. They wait here and are written the moment there
   * is, the same way the agenda form holds files picked before its talk
   * exists.
   */
  const [groups, setGroups] = useState<SubEvent[]>([]);
  const [pendingGroups, setPendingGroups] = useState<string[]>([]);

  const loadGroups = useCallback(async (eventId: string) => {
    try {
      setGroups(await apiClient.getSubEvents(eventId));
    } catch {
      // Headings are a way of reading the day, not the day.
    }
  }, []);

  const savedId = saved?.id;
  useEffect(() => { if (savedId) loadGroups(savedId); }, [savedId, loadGroups]);

  const addGroup = async (title: string) => {
    if (!saved) {
      setPendingGroups((held) => [...held, title]);
      return;
    }
    try {
      await apiClient.createSubEvent(saved.id, { title });
      await loadGroups(saved.id);
    } catch (e: any) {
      toast.error(errorText(e, t({
        ne: 'उपवर्ग थप्न सकिएन', en: 'Could not add the subcategory',
      })));
    }
  };

  const removeGroup = async (at: number) => {
    if (at >= groups.length) {
      const held = at - groups.length;
      setPendingGroups((all) => all.filter((_, i) => i !== held));
      return;
    }
    const group = groups[at];
    if (!saved || !group) return;
    try {
      await apiClient.deleteSubEvent(saved.id, group.id);
      await loadGroups(saved.id);
      // The talks under it stay on the programme; the server detaches
      // them rather than taking them with it.
      const fresh = await apiClient.getEvent(saved.id);
      setSaved(fresh);
    } catch (e: any) {
      toast.error(errorText(e, t({
        ne: 'उपवर्ग हटाउन सकिएन', en: 'Could not remove the subcategory',
      })));
    }
  };

  const groupTitles = [...groups.map((one) => one.title), ...pendingGroups];

  const [roles, setRoles] = useState<RoleGrantRow[]>([]);
  const [invited, setInvited] = useState<{ email: string; joined: boolean }[]>([]);
  const [addCoHost, setAddCoHost] = useState(false);
  const [sharing, setSharing] = useState<{ id: string; code: string } | null>(null);

  const loadPeople = useCallback(async (eventId: string) => {
    try {
      const [grants, invites] = await Promise.all([
        apiClient.getProgrammeRoles(eventId),
        apiClient.getEventInvites(eventId),
      ]);
      setRoles(grants.granted);
      setInvited(invites.invited.map((r) => ({ email: r.email, joined: r.joined })));
    } catch {
      // Lists are context, not the point of the step.
    }
  }, []);

  useEffect(() => {
    if (step === LAST && saved) loadPeople(saved.id);
  }, [step, saved, loadPeople]);

  /** Step one: the event itself. Nothing else can exist before it does. */
  const saveDetails = async () => {
    if (!title.trim()) {
      toast.error(t({ ne: 'कार्यक्रमको नाम लेख्नुहोस्', en: 'Give the event a name' }));
      return;
    }
    if (!startsAt) {
      toast.error(t({ ne: 'सुरु हुने समय दिनुहोस्', en: 'Say when it starts' }));
      return;
    }
    if (endsAt && +new Date(endsAt) <= +new Date(startsAt)) {
      toast.error(t({
        ne: 'अन्त्य सुरुभन्दा पछि हुनुपर्छ',
        en: 'The end has to come after the start',
      }));
      return;
    }

    const from = new Date(
      hasOpened && saved ? saved.scheduled_start : startsAt
    );
    // The day is the day it starts on; there is no separate date to
    // disagree with the hours any more.
    const day = toLocalInput(from).slice(0, 10);
    const minutes = endsAt
      ? Math.max(5, Math.round((+new Date(endsAt) - +from) / 60000))
      : 60;

    try {
      setBusy(true);
      let made: Event;
      if (saved) {
        made = await apiClient.updateEvent(saved.id, {
          title: title.trim(),
          venue: venue.trim(),
          description,
          ...(!hasOpened && {
            event_date: day,
            scheduled_start: from.toISOString(),
          }),
          scheduled_end: new Date(+from + minutes * 60000).toISOString(),
        });
        setSaved(made);
      } else {
        made = await apiClient.createEvent({
          title: title.trim(),
          venue: venue.trim(),
          description,
          event_date: day,
          scheduled_start: from.toISOString(),
          duration_minutes: minutes,
          sessions: [],
        });
        setSaved(made);
        toast.success(t({ ne: `${made.title} बन्यो`, en: `${made.title} created` }));
      }

      // Now there is something to hang the headings off. One that will
      // not write does not undo the event that was just made, so it is
      // said out loud rather than thrown.
      if (pendingGroups.length > 0) {
        try {
          for (const name of pendingGroups) {
            await apiClient.createSubEvent(made.id, { title: name });
          }
          setPendingGroups([]);
        } catch (e: any) {
          toast.error(errorText(e, t({
            ne: 'कार्यक्रम बच्यो, तर उपवर्ग थपिएनन्',
            en: 'The event was saved, but its subcategories were not added',
          })));
        }
        await loadGroups(made.id);
      }

      await onSaved();
      setStep(1);
    } catch (e: any) {
      toast.error(errorText(e, t({ ne: 'बचत गर्न सकिएन', en: 'Could not save it' })));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Leaving the last step without finishing it.
   *
   * By the time step three is reached the event exists - it was written
   * at step one, so the running order and the people had something to
   * hang off. What it does not have is a host who has said they are done
   * with it, so anything other than Done files it under drafts: the back
   * link, the stepper, walking off to another page, and the button that
   * used to say Cancel.
   *
   * An event that has started, or ended, is not a draft whatever the
   * form does, so only a scheduled one is moved.
   */
  const asDraft = useCallback(async () => {
    if (!saved || saved.status !== 'scheduled') return;
    try {
      const next = await apiClient.updateEvent(saved.id, { status: 'draft' });
      setSaved(next);
      await onSaved();
    } catch {
      // Leaving is not the moment to argue about it.
    }
  }, [saved, onSaved]);

  const leave = async () => { await asDraft(); onClose(); };

  /** Said once, by Done, so walking away afterwards does not undo it. */
  const finished = useRef(false);
  const done = async () => {
    finished.current = true;
    if (saved?.status === 'draft') {
      try {
        setSaved(await apiClient.updateEvent(saved.id, { status: 'scheduled' }));
        await onSaved();
      } catch (e: any) {
        toast.error(errorText(e, t({ ne: 'बचत गर्न सकिएन', en: 'Could not save it' })));
        return;
      }
    }
    onClose();
  };

  /**
   * Walking off to another page, which unmounts the form rather than
   * closing it. The cleanup reads the last state through a ref, since the
   * one it closed over is whatever it was when the effect was set up.
   */
  const latest = useRef({ step, saved });
  latest.current = { step, saved };
  useEffect(() => () => {
    const { step: at, saved: made } = latest.current;
    if (finished.current || at !== LAST || !made || made.status !== 'scheduled') return;
    apiClient.updateEvent(made.id, { status: 'draft' }).catch(() => {});
  }, []);

  /** Step two: one event's worth of running order at a time. */
  const sessionCount = saved?.sessions?.length ?? 0;

  /**
   * The days the event runs over, and which one is being worked on.
   *
   * An event that spans more than one day has a running order per day
   * rather than one long list: a talk at nine belongs to the morning of
   * a particular day, and a form that only knows a time cannot say
   * which. One day and there is nothing to choose between, so the tabs
   * do not appear at all.
   */
  const days = useMemo(() => (saved ? daysOf(saved) : []), [saved]);
  const [onDay, setOnDay] = useState('');
  const currentDay = onDay && days.includes(onDay) ? onDay : (days[0] ?? '');

  /**
   * Where the running order has reached, offered as the next start.
   *
   * The dialog asks for a time rather than working one out, so it is
   * handed the end of the last talk: a morning is usually typed in order,
   * and re-reading the clock for every row is the tedious part.
   */
  const nextFreeTime = (() => {
    const order = [...(saved?.sessions ?? [])].sort(
      (a, b) => +new Date(a.starts_at) - +new Date(b.starts_at)
    );
    const last = order[order.length - 1];
    const from = last
      ? new Date(+new Date(last.starts_at) + last.duration_minutes * 60000)
      : saved?.scheduled_start
      ? new Date(saved.scheduled_start)
      : null;
    if (!from) return undefined;
    return `${String(from.getHours()).padStart(2, '0')}:${String(from.getMinutes()).padStart(2, '0')}`;
  })();
  const coHosts = roles.filter((r) => r.role === 'co_host');
  // The event is the room, so it is its own invitation target.
  const opener = saved;

  const hostName = [user?.first_name, user?.last_name]
    .filter(Boolean).join(' ').trim();

  const dropCoHost = async (grant: RoleGrantRow) => {
    if (!saved) return;
    try {
      await apiClient.revokeRole(saved.id, grant.id);
      toast.success(t({ ne: 'हटाइयो', en: 'Removed' }));
      await loadPeople(saved.id);
    } catch (e: any) {
      toast.error(errorText(e, t({ ne: 'हटाउन सकिएन', en: 'Could not remove them' })));
    }
  };

  const withdraw = async (email: string) => {
    if (!saved) return;
    try {
      await apiClient.withdrawEventInvite(saved.id, email);
      toast.success(t({ ne: 'निम्तो फिर्ता', en: 'Invitation withdrawn' }));
      await loadPeople(saved.id);
    } catch (e: any) {
      toast.error(errorText(e, t({ ne: 'हटाउन सकिएन', en: 'Could not take them off' })));
    }
  };

  const skip = step < LAST && saved
    ? (
      <button
        onClick={() => (step === LAST ? leave() : setStep(step + 1))}
        className="text-[14px] text-tagink underline hover:no-underline"
      >
        {t({ ne: 'छोड्नुहोस्', en: 'Skip' })}
      </button>
    )
    : null;

  return (
    <Sheet>
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-4">
          <BackLink
            label={{ ne: 'कार्यक्रम', en: 'Event' }}
            onClick={() => (step === LAST ? leave() : onClose())}
          />
          {skip && <span className="ml-auto">{skip}</span>}
        </div>

        <EventHeading
          title={title || t({ ne: 'नयाँ कार्यक्रम', en: 'New event' })}
          under={saved ? [whenLine(saved), saved.venue].filter(Boolean).join(' · ')
                       : t({ ne: 'तीन चरणमा तयार गर्नुहोस्', en: 'Set it up in four steps' })}
        />

        <Stepper
          steps={STEPS}
          at={step}
          onGo={(i) => {
            if (!saved || i === step) return;
            if (step === LAST) asDraft();
            setStep(i);
          }}
        />
      </div>

      {step === 0 && (
        <div className="flex flex-col gap-5 max-w-[720px]">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-[20px] font-medium text-head">
                {t({ ne: 'कार्यक्रमको विवरण', en: 'Event Details' })}
              </h2>
              <p className="text-[14px] text-body mt-1">
                {t({
                  ne: 'यो कार्यक्रम के हो, कहिले र कहाँ हुन्छ।',
                  en: 'What this event is, and when and where it happens.',
                })}
              </p>
            </div>
          </div>

          <Field label={t({ ne: 'कार्यक्रमको नाम', en: 'Event name' })} required>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t({ ne: 'कताहो आकस्मिक सेवा कार्यक्रम', en: 'Kataho Emergency Services Event' })}
              className={inputClass}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t({ ne: 'सुरु', en: 'Starts' })} required>
              <input
                type="datetime-local"
                value={startsAt}
                disabled={hasOpened}
                onChange={(e) => {
                  setStartsAt(e.target.value);
                  // Keep the end ahead of the start rather than letting
                  // the two cross over while somebody is typing.
                  if (e.target.value && +new Date(endsAt) <= +new Date(e.target.value)) {
                    setEndsAt(plusAnHour(e.target.value));
                  }
                }}
                className={`${inputClass} ${
                  hasOpened ? 'bg-[#f5f5f5] text-subtle cursor-not-allowed' : ''
                }`}
              />
              {hasOpened && (
                <p className="pt-1 text-[12px] text-subtle">
                  {t({
                    ne: 'कार्यक्रम सुरु भइसक्यो, त्यसैले सुरुको समय बदल्न मिल्दैन।',
                    en: 'This event has already started, so its start time cannot be moved.',
                  })}
                </p>
              )}
            </Field>
            <Field label={t({ ne: 'अन्त्य', en: 'Ends' })}>
              <input
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
          <p className="-mt-2 text-[12.5px] text-subtle">
            {t({
              ne: 'अन्त्यको समय औपचारिक हो — कार्यक्रम आयोजकले नसकाएसम्म चलिरहन्छ।',
              en: 'The end is a formality: the event runs until the host ends it.',
            })}
          </p>

          <Field label={t({ ne: 'स्थान', en: 'Venue' })}>
            <input
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              placeholder={t({ ne: 'रामलक्ष्मण इनोवेशन', en: 'RamLaxman Innovations' })}
              className={inputClass}
            />
          </Field>

          <Field label={t({ ne: 'विवरण', en: 'Description' })} hint={t({ ne: 'वैकल्पिक', en: 'optional' })}>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t({ ne: 'कताहो इमर्जेन्सी सर्भिस (आपत्कालीन सेवा) नेपालमा कताहो डिजिटल एड्रेस सिस्टमको....', en: 'Kataho Emergency Services is a key feature of the Kataho Digital Address System.....' })}
              className={inputClass}
            />
          </Field>

          {/* The parts the day divides into, named here so the running
              order on the next step can be read under them. */}
          <SubcategoryEditor
            titles={groupTitles}
            onAdd={addGroup}
            onRemove={removeGroup}
            disabled={busy}
          />

          <Actions
            cancel={t({ ne: 'रद्द', en: 'Cancel' })}
            go={busy
              ? t({ ne: 'बचत गर्दै…', en: 'Saving…' })
              : saved
              ? t({ ne: 'परिवर्तन बचत', en: 'Save changes' })
              : t({ ne: 'कार्यक्रम बनाउनुहोस्', en: 'Create event' })}
            onCancel={onClose}
            onGo={saveDetails}
            disabled={busy}
          />
        </div>
      )}

      {step === 1 && (
        <div className="flex flex-col gap-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-[20px] font-medium text-head">
                {t({ ne: 'कार्यसूची', en: 'Agendas' })}
              </h2>
              <p className="text-[14px] text-body mt-1">
                {t({
                  ne: 'यो कार्यक्रमका कार्यसूची र वक्ता मिलाउनुहोस्।',
                  en: 'Organize the agendas and speakers for this event.',
                })}
              </p>
            </div>
            {!drafting && (
              <Btn
                tone="solid"
                className="px-5 py-3 text-[16px]"
                onClick={() => setDrafting(true)}
              >
                <PlusGlyph />
                {t({ ne: 'नयाँ कार्यसूची बनाउनुहोस्', en: 'Create New Agendas' })}
              </Btn>
            )}
          </div>

          {/* Which day is being laid out. Only where there is more than
              one: a single-day event has nothing to choose between. */}
          {saved && days.length > 1 && (
            <div role="tablist" className="flex gap-1 border-b border-line flex-wrap">
              {days.map((one, i) => {
                const on = one === currentDay;
                const count = (saved.sessions ?? [])
                  .filter((s) => dayOfSession(s) === one).length;
                return (
                  <button
                    key={one}
                    role="tab"
                    aria-selected={on}
                    onClick={() => setOnDay(one)}
                    className={`flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2
                      text-[14px] font-medium whitespace-nowrap ${
                        on
                          ? 'border-head text-head'
                          : 'border-transparent text-subtle hover:text-body'
                      }`}
                  >
                    {t({ ne: `दिन ${num(i + 1)}`, en: `Day ${i + 1}` })}
                    <span className="bg-[#F3F4F6] rounded-full px-1.5 text-[12px]
                      font-medium leading-[19px] text-[#364153]">
                      {num(count)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* What is already in the running order, and arranging it. */}
          {saved && sessionCount > 0 && (
            <section className="bg-wash rounded-[12px] p-5">
              <h2 className="text-[12px] font-medium tracking-[.06em] uppercase
                text-subtle mb-4">
                {t({
                  ne: `एजेन्डा · ${num(sessionCount)} सत्र`,
                  en: `Agenda · ${sessionCount} sessions`,
                })}
              </h2>
              <GroupedAgenda
                event={saved}
                groups={groups}
                sessions={days.length > 1
                  ? (saved.sessions ?? [])
                      .filter((one) => dayOfSession(one) === currentDay)
                  : undefined}
                onChanged={async () => {
                  const fresh = await apiClient.getEvent(saved.id);
                  setSaved(fresh);
                  await onSaved();
                }}
                onAdd={() => setDrafting(true)}
                onEdit={(id) => {
                  const one = (saved.sessions ?? []).find((s) => s.id === id);
                  if (one) setEditing(one);
                }}
              />
            </section>
          )}

          <Actions
            cancel={t({ ne: 'रद्द', en: 'Cancel' })}
            go={t({ ne: 'जारी राख्नुहोस्', en: 'Continue' })}
            onCancel={onClose}
            onGo={() => setStep(2)}
          />

          {(drafting || editing) && saved && (
            <AddAgendaDialog
              eventId={saved.id}
              // The day whose tab is open, so what is created lands where
              // the host is looking rather than always on the first day.
              day={currentDay
                || saved.event_date
                || toLocalInput(new Date(saved.scheduled_start)).slice(0, 10)}
              days={days}
              session={editing ?? undefined}
              suggestedStart={nextFreeTime}
              onClose={() => { setDrafting(false); setEditing(null); }}
              onAdded={async () => {
                setDrafting(false);
                setEditing(null);
                const fresh = await apiClient.getEvent(saved.id);
                setSaved(fresh);
                await onSaved();
              }}
            />
          )}
        </div>
      )}

      {step === 2 && saved && (
        <div className="flex flex-col gap-5">
          <SpeakersStep
            event={saved}
            sessions={saved.sessions ?? []}
            onChanged={async () => {
              const fresh = await apiClient.getEvent(saved.id);
              setSaved(fresh);
              await onSaved();
            }}
          />
          <Actions
            cancel={t({ ne: 'रद्द', en: 'Cancel' })}
            go={t({ ne: 'जारी राख्नुहोस्', en: 'Continue' })}
            onCancel={onClose}
            onGo={() => setStep(3)}
          />
        </div>
      )}

      {step === 3 && saved && (
        <div className="flex flex-col gap-5">
          <div>
            <h2 className="text-[20px] font-medium text-head">
              {t({ ne: 'मानिस', en: 'Peoples' })}
            </h2>
            <p className="text-[14px] text-body mt-1.5">
              {t({
                ne: 'यो कार्यक्रमका सहभागी, वक्ता र सह-आयोजक व्यवस्थापन गर्नुहोस्।',
                en: 'Manage attendees, speakers and co-hosts for the event',
              })}
            </p>
          </div>

          {/* One list, read top to bottom: who owns the event, who runs it
              beside them, and who is coming. The two side-by-side cards
              this replaces asked the eye to start twice. */}
          <div className="flex flex-col">
            <PeopleHeading label={{ ne: 'आयोजक', en: 'Host' }} />
            <PersonRow
              initials={initialsOf(hostName, saved.host_email)}
              dark
              name={hostName || (saved.host_email ?? '')}
              suffix={t({ ne: '(तपाईं)', en: '(you)' })}
              email={saved.host_email ?? ''}
              tag={t({ ne: 'आयोजक', en: 'Host' })}
            />

            <PeopleHeading
              label={{
                ne: `सह-आयोजक · ${num(coHosts.length)}`,
                en: `Co-hosts · ${coHosts.length}`,
              }}
              action={{
                label: { ne: '+ सह-आयोजक थप्नुहोस्', en: '+ Add co-host' },
                onClick: () => setAddCoHost(true),
              }}
            />
            {coHosts.length === 0 ? (
              <PeopleEmpty>
                {t({
                  ne: 'सह-आयोजकले तपाईंसँगै यो कार्यक्रम चलाउन सक्छन्।',
                  en: 'A co-host can run this event beside you.',
                })}
              </PeopleEmpty>
            ) : (
              coHosts.map((one) => (
                <PersonRow
                  key={one.id}
                  initials={initialsOf('', one.email)}
                  name={one.email.split('@')[0]}
                  email={one.email}
                  tag={t({ ne: 'सह-आयोजक', en: 'Co-host' })}
                  onRemove={() => dropCoHost(one)}
                />
              ))
            )}

            <PeopleHeading
              label={{ ne: 'सहभागी', en: 'Attendees' }}
              action={
                opener
                  ? {
                      label: { ne: '+ सहभागीलाई निम्तो', en: '+ Invite attendees' },
                      onClick: () => setSharing(opener),
                    }
                  : undefined
              }
            />
            {invited.length === 0 ? (
              <PeopleEmpty>
                {t({
                  ne: 'अझै कसैलाई निम्तो छैन।',
                  en: 'No attendees invited yet.',
                })}
              </PeopleEmpty>
            ) : (
              invited.map((row) => (
                <PersonRow
                  key={row.email}
                  initials={initialsOf('', row.email)}
                  name={row.email.split('@')[0]}
                  email={row.email}
                  tag={row.joined ? t({ ne: 'आइसके', en: 'Joined' }) : undefined}
                  onRemove={() => withdraw(row.email)}
                />
              ))
            )}
          </div>

          {/* Nothing is cancelled from here: the event exists already, so
              the choice is between finishing it and leaving it a draft. */}
          <Actions
            cancel={t({ ne: 'मस्यौदाका रूपमा बचत', en: 'Save as draft' })}
            go={t({ ne: 'सकियो', en: 'Done' })}
            onCancel={leave}
            onGo={done}
          />
        </div>
      )}

      {addCoHost && saved && (
        <CoHostDialog
          eventId={saved.id}
          onClose={() => setAddCoHost(false)}
          onAdded={async () => { setAddCoHost(false); await loadPeople(saved.id); }}
        />
      )}

      {sharing && (
        <ShareEventDialog
          eventId={sharing.id}
          eventCode={sharing.code}
          onClose={() => setSharing(null)}
          onInvited={() => { if (saved) loadPeople(saved.id); }}
        />
      )}
    </Sheet>
  );
};
